// src/components/Footer.jsx
import React from 'react';
import { motion } from 'framer-motion';
import styles from './Footer.module.css'; // Ensure this path is correct based on your setup
// import logo from '../../assets/imgs/Logo.png'; // Assuming a light logo for dark footer background
import logo_d from "../../assets/img/Logo_light.png"; // Using logo_d for the footer

import {
  FaPhoneAlt,
  FaEnvelope,
  FaChevronRight,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaArrowUp
} from 'react-icons/fa';

const Footer = () => {
  const usefulLinks = [
    { label: "Home", href: "/" },
    { label: "About us", href: "/about" },
    { label: "Bspark Play Ground", href: "https://playground.bsparksoftwaretechnologies.com/" },
    { label: "Services", href: "#" },
    { label: "Terms of service", href: "#" },
    { label: "Privacy policy", href: "#" },
  ];

  const devLinks = [
    { label: "Java", href: "/courses/java-programming-fundamentals" },
    { label: "Python", href: "/courses/python-programming-fundamentals" },
    { label: "HTML & CSS", href: "/courses/full-stack-web-development" },
    { label: "React JS", href: "/courses/full-stack-web-development" },
    { label: "Node JS", href: "#" },
    { label: "Full Stack", href: "/courses/full-stack-web-development" },
    { label: "SQL", href: "/courses/sql-database-fundamentals" },
    { label: "AWS", href: "/courses/cloud-computing-essentials" },
    { label: "Salesforce", href: "#" },
    { label: "Azure", href: "/courses/cloud-computing-essentials" },
    { label: "Appian", href: "#" },
  ];

  const testingLinks = [
    { label: "Selenium", href: "/courses/selenium-test-automation" },
    { label: "API Automation", href: "/courses/api-testing-fundamentals" },
    { label: "Mobile Appium", href: "https://www.bsparksoftwaretechnologies.com/appium-training-in-chennai" },
    { label: "JMeter", href: "https://www.bsparksoftwaretechnologies.com/jmeter-training-in-chennai" },
    { label: "ETL Testing", href: "#" },
    { label: "Software Testing", href: "/courses/software-test-automation" },
    { label: "Playwright", href: "/courses/playwright-test-automation" },
  ];

  const trendingLinks = [
    { label: "Selenium in Chennai", href: "/courses/selenium-test-automation" },
    { label: "Playwright in Chennai", href: "/courses/playwright-test-automation" },
    { label: "API Automation in Chennai", href: "/courses/api-testing-fundamentals" },
    { label: "Software Testing in Chennai", href: "/courses/software-test-automation" },
  ];

  // Animation variants for Framer Motion
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15, // Stagger animation for child columns
        delayChildren: 0.2
      }
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    },
  };

  const linkItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
  };


  const Section = ({ title, links }) => (
    <motion.div
      className={styles.footerColumn}
      variants={itemVariants} // Apply item variants to columns
    >
      <h4 className={styles.sectionTitle}>{title}</h4>
      <ul className={styles.linkList}>
        {links.map((link, index) => (
          <motion.li
            key={index}
            className={styles.linkItem}
            variants={linkItemVariants} // Apply variants to individual links for staggered effect
          >
            <FaChevronRight className={styles.icon} />
            <a href={link.href} target="_blank" rel="noreferrer">{link.label}</a>
          </motion.li>
        ))}
      </ul>
    </motion.div>
  );

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={styles.footerWrapper}>
      <motion.footer
        className={styles.footer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }} // Trigger when 30% of element is in view
        variants={containerVariants} // Apply container variants to the footer
      >
        <div className={styles.footerTop}>
          <div className={styles.footerContainer}>
            <motion.div
              className={styles.footerColumn}
              variants={itemVariants} // Apply item variants
            >
              <img
                src={logo_d} // Using the light logo for the dark background
                alt="Bspark-logo"
                className={styles.logo}
              />
              <p className={styles.description}>
                Empowering the next generation of tech professionals with cutting-edge skills and career opportunities.
              </p>
              <p className={styles.contact}>
                <a href="tel:+91-7200702908"><FaPhoneAlt /> +91-7200702908</a><br />
                <a href="mailto:info@bsparksoftwaretechnologies.com"><FaEnvelope /> info@bsparksoftwaretechnologies.com</a>
              </p>

              <div className={styles.socials}>
                <motion.a href="https://www.facebook.com/bsparksoftwaretechnologies/" target="_blank" whileHover={{ scale: 1.2, color: '#1877F2' }} transition={{ type: "spring", stiffness: 400, damping: 10 }}><FaFacebookF /></motion.a>
                <motion.a href="https://www.instagram.com/bsparksoftwaretechnologies" target="_blank" whileHover={{ scale: 1.2, color: '#E4405F' }} transition={{ type: "spring", stiffness: 400, damping: 10 }}><FaInstagram /></motion.a>
                <motion.a href="https://www.linkedin.com/company/bspark-software-technologies" target="_blank" whileHover={{ scale: 1.2, color: '#0A66C2' }} transition={{ type: "spring", stiffness: 400, damping: 10 }}><FaLinkedinIn /></motion.a>
              </div>
            </motion.div>

            <Section title="Useful Links" links={usefulLinks} />
            <Section title="Software Development" links={devLinks} />
            <Section title="Software Testing" links={testingLinks} />
            <Section title="Trending Courses" links={trendingLinks} /> {/* Changed to "Trending Courses" */}
          </div>
        </div>

        <div className={styles.footerBottom}>
          <p>&copy; {new Date().getFullYear()} <strong>Bspark Software Technologies Pvt Ltd.</strong> All Rights Reserved</p>
          <motion.button
            className={styles.scrollTop}
            onClick={scrollToTop}
            whileHover={{ scale: 1.1, backgroundColor: '#ef921a' }}
            whileTap={{ scale: 0.9 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <FaArrowUp />
          </motion.button>
        </div>
      </motion.footer>
    </div>
  );
};

export default Footer;